import axios from 'axios';

// 2026-06-05 — SaaS: read backend URL from REACT_APP_API_URL env var
// injected at build time by Cloudflare Pages. Falls back to api.zeroriskpay.com
// if the var is missing.
const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.zeroriskpay.com').replace(/\/$/, '');
const api = axios.create({ baseURL: `${API_BASE}/api` });

// Inject JWT token on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401 (expired/invalid token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export const gatewaysApi = {
  getAll: () => api.get('/gateways').then(r => r.data),
  create: (data) => api.post('/gateways', data).then(r => r.data),
  update: (id, data) => api.put(`/gateways/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/gateways/${id}`).then(r => r.data),
};

// 2026-10-15 — Varias vistas piden TODOS los pagos al montar (hasta 5 veces
// por carga del panel, ~26 MB sin comprimir cada una). Compartimos una única
// descarga en vuelo y la reutilizamos 5 s; cualquier mutación la invalida.
let _paymentsListCache = null; // { at, promise }
const _invalidatePayments = () => { _paymentsListCache = null; };
const _getAllPayments = (params) => {
  if (params && Object.keys(params).length) return api.get('/payments', { params }).then(r => r.data);
  const now = Date.now();
  if (_paymentsListCache && now - _paymentsListCache.at < 5000) return _paymentsListCache.promise;
  const promise = api.get('/payments').then(r => r.data).catch(e => { _paymentsListCache = null; throw e; });
  _paymentsListCache = { at: now, promise };
  return promise;
};

export const paymentsApi = {
  getAll: _getAllPayments,
  invalidate: _invalidatePayments,
  process: (data) => { _invalidatePayments(); return api.post('/payments', data).then(r => r.data); },
  refund: (id) => { _invalidatePayments(); return api.post(`/payments/${id}/refund`).then(r => r.data); },
  // safeRevert=true skips the re-charge (used for false-positive sync refunds
  // where the gateway never actually refunded — see refundedFromBalanceSync).
  undoRefund: (id, { safeRevert = false } = {}) => {
    _invalidatePayments();
    return api.post(`/payments/${id}/undo-refund${safeRevert ? '?safeRevert=1' : ''}`).then(r => r.data);
  },
  getKpis: () => api.get('/dashboard/kpis').then(r => r.data),
  getRentability: (params = {}) => api.get('/dashboard/rentability', { params }).then(r => r.data),
  getCampaignAnalysis: (params = {}) => api.get('/dashboard/campaign-analysis', { params }).then(r => r.data),
  getTopProducts: (params = {}) => api.get('/dashboard/top-products', { params }).then(r => r.data),
};

// Tracking module — Tracking branded auto-tracking
export const trackingApi = {
  dashboard:        ()       => api.get('/tracking/dashboard').then(r => r.data),
  listShipments:    (params) => api.get('/tracking/shipments', { params }).then(r => r.data),
  getShipment:      (id)     => api.get(`/tracking/shipments/${id}`).then(r => r.data),
  createShipment:   (data)   => api.post('/tracking/shipments', data).then(r => r.data),
  updateShipment:   (id, p)  => api.patch(`/tracking/shipments/${id}`, p).then(r => r.data),
  deleteShipment:   (id)     => api.delete(`/tracking/shipments/${id}`).then(r => r.data),
  releaseShipment:  (id)     => api.post(`/tracking/shipments/${id}/release`).then(r => r.data), // pre-order → enviar ahora
  addEvent:         (id, e)  => api.post(`/tracking/shipments/${id}/events`, e).then(r => r.data),
  bulkAddEvent:     (data)   => api.post('/tracking/shipments/bulk-events', data).then(r => r.data),
  deleteEvent:      (eid)    => api.delete(`/tracking/events/${eid}`).then(r => r.data),
  statusCatalog:    ()       => api.get('/tracking/status-catalog').then(r => r.data),
  getSettings:      ()       => api.get('/tracking/settings').then(r => r.data),
  saveSettings:     (data)   => api.put('/tracking/settings', data).then(r => r.data),
};

export const cleanupApi = {
  previewAbandoned: (days = 90) => api.get('/cleanup/abandoned/preview', { params: { days } }).then(r => r.data),
  deleteAbandoned: (days = 90) => api.post('/cleanup/abandoned', { days }).then(r => r.data),
  pendingConfirmations: () => api.get('/cleanup/pending-confirmations').then(r => r.data),
  resendConfirmations: (limit = 50) => api.post('/cleanup/resend-confirmations', { limit }).then(r => r.data),
};

export const subscriptionsApi = {
  getAll: (params) => api.get('/subscriptions', { params }).then(r => r.data),
  getStats: () => api.get('/subscriptions/stats/summary').then(r => r.data),
  create: (data) => api.post('/subscriptions', data).then(r => r.data),
  fromPayment: (data) => api.post('/subscriptions/from-payment', data).then(r => r.data),
  cancel: (id) => api.delete(`/subscriptions/${id}`).then(r => r.data),
  pause: (id) => api.put(`/subscriptions/${id}/pause`).then(r => r.data),
  resume: (id) => api.put(`/subscriptions/${id}/resume`).then(r => r.data),
  declineAnalysis: () => api.get('/subscriptions/decline-analysis').then(r => r.data),
  // Vault backfill — moves raw-card RCs to TailoredPay Customer Vault
  vaultBackfillPreview: () => api.get('/subscriptions/vault-backfill/preview').then(r => r.data),
  vaultBackfillRun: (limit) => api.post('/subscriptions/vault-backfill/run', limit ? { limit } : {}).then(r => r.data),
  vaultBackfillAudit: () => api.get('/subscriptions/vault-backfill/audit').then(r => r.data),
  upcomingChargesAudit: (days = 7) => api.get('/subscriptions/vault-backfill/upcoming-charges-audit', { params: { days } }).then(r => r.data),
};

export const batchApi = {
  fetchCustomers: (data) => api.post('/batch/customers', data).then(r => r.data),
  charge: (data) => api.post('/batch/charge', data).then(r => r.data),
};

// Customers — aggregated from payment history, one row per unique buyer.
export const customersApi = {
  getAll: (params) => api.get('/customers', { params }).then(r => r.data),
};

// Live funnel — real-time visitors, funnel stages, geo and activity feed.
// `window` is one of now | 10m | 1h | today | 24h.
export const funnelApi = {
  live: (params) => api.get('/funnel/live', { params }).then(r => r.data),
};

// Gateway health — per-account approval/chargeback rates, recommended daily
// capacity and threshold alerts. Guidance only, never a guarantee.
export const gatewayHealthApi = {
  get: (params) => api.get('/gateway-health', { params }).then(r => r.data),
};

// Batch payments — history, detail and the spread-over-minutes scheduler.
// The legacy fetchCustomers/charge live in batchApi above and are unchanged.
export const batchHistoryApi = {
  list:    (params) => api.get('/batch', { params }).then(r => r.data),
  get:     (id)     => api.get(`/batch/${id}`).then(r => r.data),
  stop:    (id)     => api.post(`/batch/${id}/stop`).then(r => r.data),
  preview: (data)   => api.post('/batch/preview', data).then(r => r.data),
};

// SMS — templates, campaigns and history. Sending requires a provider
// (Twilio / SMS.to) configured per tenant; without one every send is
// recorded as Failed with PROVIDER_DISABLED rather than silently dropped.
export const smsApi = {
  variables:        ()          => api.get('/sms/variables').then(r => r.data),
  audience:         (params)    => api.get('/sms/audience', { params }).then(r => r.data),

  listTemplates:    (params)    => api.get('/sms/templates', { params }).then(r => r.data),
  getTemplate:      (id)        => api.get(`/sms/templates/${id}`).then(r => r.data),
  createTemplate:   (data)      => api.post('/sms/templates', data).then(r => r.data),
  updateTemplate:   (id, data)  => api.put(`/sms/templates/${id}`, data).then(r => r.data),
  deleteTemplate:   (id)        => api.delete(`/sms/templates/${id}`).then(r => r.data),
  previewTemplate:  (data)      => api.post('/sms/templates/preview', data).then(r => r.data),

  listCampaigns:    (params)    => api.get('/sms/campaigns', { params }).then(r => r.data),
  getCampaign:      (id)        => api.get(`/sms/campaigns/${id}`).then(r => r.data),
  createCampaign:   (data)      => api.post('/sms/campaigns', data).then(r => r.data),
  updateCampaign:   (id, data)  => api.put(`/sms/campaigns/${id}`, data).then(r => r.data),
  deleteCampaign:   (id)        => api.delete(`/sms/campaigns/${id}`).then(r => r.data),
  estimate:         (data)      => api.post('/sms/campaigns/estimate', data).then(r => r.data),
  estimateCampaign: (id)        => api.post(`/sms/campaigns/${id}/estimate`).then(r => r.data),
  sendCampaign:     (id)        => api.post(`/sms/campaigns/${id}/send`).then(r => r.data),
  cancelCampaign:   (id)        => api.post(`/sms/campaigns/${id}/cancel`).then(r => r.data),
  campaignMetrics:  (id)        => api.get(`/sms/campaigns/${id}/metrics`).then(r => r.data),

  listHistory:      (params)    => api.get('/sms/history', { params }).then(r => r.data),
  historyStats:     (params)    => api.get('/sms/history/stats', { params }).then(r => r.data),
  getHistory:       (id)        => api.get(`/sms/history/${id}`).then(r => r.data),

  getProvider:      ()          => api.get('/sms/provider').then(r => r.data),
  saveProvider:     (data)      => api.put('/sms/provider', data).then(r => r.data),
  deleteProvider:   ()          => api.delete('/sms/provider').then(r => r.data),
  testProvider:     (to)        => api.post('/sms/provider/test', { to }).then(r => r.data),
};

export const shopsApi = {
  getAll: () => api.get('/shops').then(r => r.data),
  // Logo servido por el backend (la lista de tiendas no incluye logoData por tamaño)
  logoUrl: (id, bust) => `${API_BASE}/api/shops/${id}/logo${bust ? `?v=${bust}` : ''}`,
  create: (data) => api.post('/shops', data).then(r => r.data),
  update: (id, data) => api.put(`/shops/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/shops/${id}`).then(r => r.data),
  // 2026-10-21 — chequeador de webs
  siteStatus: () => api.get('/shops/site-status').then(r => r.data),
  siteCheck: (id) => api.post(`/shops/${id}/site-check`).then(r => r.data),
  setSiteAlerts: (emails) => api.put('/shops/site-alerts', { emails }).then(r => r.data),
};

export const webhooksApi = {
  getAll: () => api.get('/webhooks').then(r => r.data),
  create: (data) => api.post('/webhooks', data).then(r => r.data),
  update: (id, data) => api.put(`/webhooks/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/webhooks/${id}`).then(r => r.data),
  getLogs: () => api.get('/webhooks/logs').then(r => r.data),
};

export const pixelsApi = {
  getAll: () => api.get('/pixels').then(r => r.data),
  create: (data) => api.post('/pixels', data).then(r => r.data),
  delete: (id) => api.delete(`/pixels/${id}`).then(r => r.data),
  getConfig: (shopId) => api.get('/pixels/config', { params: shopId ? { shopId } : {} }).then(r => r.data),
  saveConfig: (data) => api.put('/pixels/config', data).then(r => r.data),
  getShopsStatus: () => api.get('/pixels/shops-status').then(r => r.data),
  migrateToShop: (shopId) => api.post('/pixels/migrate-to-shop', { shopId }).then(r => r.data),
  backfill: (data) => api.post('/pixels/backfill', data).then(r => r.data),
  testTikTokLive: (data) => api.post('/pixels/test-tiktok-live', data).then(r => r.data),
};

export const templatesApi = {
  getAll: () => api.get('/templates').then(r => r.data),
  create: (data) => api.post('/templates', data).then(r => r.data),
  update: (id, data) => api.put(`/templates/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/templates/${id}`).then(r => r.data),
  reseed: () => api.post('/templates/reseed').then(r => r.data),
};

export const clubApi = {
  getConfig: (shopId) => api.get('/club/config', { params: shopId ? { shopId } : {} }).then(r => r.data),
  createConfig: (data) => api.post('/club/config', data).then(r => r.data),
  updateConfig: (data) => api.put('/club/config', data).then(r => r.data),
  toggleConfig: (data) => api.patch('/club/config', data).then(r => r.data),
  deleteConfig: (shopId) => api.delete('/club/config', { params: shopId ? { shopId } : {} }).then(r => r.data),
  getRewards: (shopId) => api.get('/club/rewards', { params: shopId ? { shopId } : {} }).then(r => r.data),
  addReward: (data) => api.post('/club/rewards', data).then(r => r.data),
  updateReward: (idx, data) => api.put(`/club/rewards/${idx}`, data).then(r => r.data),
  deleteReward: (idx, shopId) => api.delete(`/club/rewards/${idx}`, { params: shopId ? { shopId } : {} }).then(r => r.data),
  getMembers: (shopId) => api.get('/club/members', { params: shopId ? { shopId } : {} }).then(r => r.data),
  getStats: (shopId) => api.get('/club/stats', { params: shopId ? { shopId } : {} }).then(r => r.data),
  deleteMember: (id) => api.delete(`/club/members/${id}`).then(r => r.data),
  resendMemberEmail: (id) => api.post(`/club/members/${id}/resend-email`).then(r => r.data),
  getShopsStatus: () => api.get('/club/shops-status').then(r => r.data),
  migrateToShop: (shopId) => api.post('/club/migrate-to-shop', { shopId }).then(r => r.data),
  generateRewards: (shopId, category) => api.post('/club/generate-rewards', { shopId, category }).then(r => r.data),
  generateRewardsAi: (shopId, category, bypassCache = false) => api.post('/club/generate-rewards-ai', { shopId, category, bypassCache }).then(r => r.data),
  // Blog
  getBlog: (shopId) => api.get('/club/blog', { params: shopId ? { shopId } : {} }).then(r => r.data),
  generateBlogAi: (shopId, category, count = 4, bypassCache = false) => api.post('/club/generate-blog-ai', { shopId, category, count, bypassCache }).then(r => r.data),
  deleteArticle: (id, shopId) => api.delete(`/club/blog/${id}`, { params: shopId ? { shopId } : {} }).then(r => r.data),
  deleteAllBlog: (shopId) => api.delete('/club/blog', { params: shopId ? { shopId } : {} }).then(r => r.data),
};

// ── Beneficios (P&L) module ────────────────────────────────────────────────
//
// Every endpoint accepts `from`, `to` (YYYY-MM-DD) and an optional `shopId`
// filter. All monetary values in the response are already in EUR (normalised
// server-side). Per-shop breakdowns include their own `currency` for display.
//
// Timezone: the server buckets dates using the user's configured timezone
// (default Europe/Madrid, configurable via PUT /beneficios/config) so the
// frontend must NOT shift dates on its own — just render what the API returns.
export const beneficiosApi = {
  // Core P&L ---------------------------------------------------------------
  summary: (params) => api.get('/beneficios/summary', { params }).then(r => r.data),
  daily: (params) => api.get('/beneficios/daily', { params }).then(r => r.data),
  rebillRate: (params) => api.get('/beneficios/rebill-rate', { params }).then(r => r.data),
  // Chargebacks summary (lives under /dashboard but exposed here for the
  // beneficios page that shows the CB rate widget alongside other metrics).
  chargebacksSummary: () => api.get('/dashboard/chargebacks-summary').then(r => r.data),
  chargebacksList: () => api.get('/dashboard/chargebacks-list').then(r => r.data),
  chargebackEvidence: (caseNumber, email) => api.get('/dashboard/chargeback-evidence', { params: { caseNumber, email } }).then(r => r.data),
  unitEconomicsDefaults: (days = 3) => api.get('/beneficios/unit-economics-defaults', { params: { days } }).then(r => r.data),
  forecast: (params) => api.get('/beneficios/forecast', { params }).then(r => r.data),

  // Config (timezone, rebillRateOverride, shopDefaults) -------------------
  getConfig: () => api.get('/beneficios/config').then(r => r.data),
  updateConfig: (data) => api.put('/beneficios/config', data).then(r => r.data),

  // Gateway fee structure -------------------------------------------------
  updateFees: (gatewayId, data) => api.put(`/gateways/${gatewayId}/fees`, data).then(r => r.data),

  // COGS (product costs) --------------------------------------------------
  listProducts: (params) => api.get('/beneficios/products', { params }).then(r => r.data),
  saveProduct: (data) => api.post('/beneficios/products', data).then(r => r.data),
  deleteProduct: (id) => api.delete(`/beneficios/products/${id}`).then(r => r.data),
  importProducts: () => api.post('/beneficios/products/import').then(r => r.data),
  bulkUpdateProducts: (ids, changes) => api.put('/beneficios/products/bulk-update', { ids, changes }).then(r => r.data),
  bulkDeleteProducts: (ids) => api.post('/beneficios/products/bulk-delete', { ids }).then(r => r.data),
  productsMatchStatus: (days = 30) => api.get('/beneficios/products/match-status', { params: { days } }).then(r => r.data),

  // Ad-spend --------------------------------------------------------------
  listAdSpend: (params) => api.get('/beneficios/adspend', { params }).then(r => r.data),
  adSpendDiag: (params) => api.get('/beneficios/adspend/diag', { params }).then(r => r.data),
  saveAdSpend: (data) => api.post('/beneficios/adspend', data).then(r => r.data),
  deleteAdSpend: (id) => api.delete(`/beneficios/adspend/${id}`).then(r => r.data),

  // Meta Ads --------------------------------------------------------------
  metaConnect: (token) => api.post('/beneficios/meta/connect', { token }).then(r => r.data),
  metaDisconnect: () => api.delete('/beneficios/meta/disconnect').then(r => r.data),
  metaAccounts: () => api.get('/beneficios/meta/accounts').then(r => r.data),
  metaCampaigns: (actId) => api.get('/beneficios/meta/campaigns', { params: { actId } }).then(r => r.data),
  metaMapping: (mapping) => api.put('/beneficios/meta/mapping', { mapping }).then(r => r.data),
  metaSync: (params) => api.post('/beneficios/meta/sync', params).then(r => r.data),
  metaResyncClean: (params) => api.post('/beneficios/meta/resync-clean', params).then(r => r.data),
  metaRefreshToday: () => api.post('/beneficios/meta/refresh-today').then(r => r.data),

  // Google Ads ------------------------------------------------------------
  googleAuthorizeUrl: (redirectUri) => api.get('/beneficios/google/authorize-url', { params: { redirectUri } }).then(r => r.data),
  googleCallback: (data) => api.post('/beneficios/google/callback', data).then(r => r.data),
  googleDisconnect: () => api.delete('/beneficios/google/disconnect').then(r => r.data),
  googleCustomers: () => api.get('/beneficios/google/customers').then(r => r.data),
  googleCampaigns: (customerId, loginCustomerId) => api.get('/beneficios/google/campaigns', { params: { customerId, loginCustomerId } }).then(r => r.data),
  googleMapping: (mapping) => api.put('/beneficios/google/mapping', { mapping }).then(r => r.data),
  googleSync: (params) => api.post('/beneficios/google/sync', params).then(r => r.data),
  googleResyncClean: (params) => api.post('/beneficios/google/resync-clean', params).then(r => r.data),

  // CSV fallback + diagnostics -------------------------------------------
  importCsv: (csv, source = 'manual') => api.post('/beneficios/import-csv', { csv, source }).then(r => r.data),
  health: () => api.get('/beneficios/health').then(r => r.data),

  // 2026-06-01 — Gateway profit: revenue REAL del gateway TailoredPay (skip
  // MongoDB cache que está desincronizado). Tabla día a día NY tz con sales,
  // refunds, adSpend, netProfit. Tarda ~40s con 7 días, no usar para periods
  // mayores hasta tener cron sync local.
  gatewayRevenueByDay: (days = 7) =>
    api.get(`/_diag/nmi-revenue-by-day`, { params: { days } }).then(r => r.data),
};

// ─────────────────────────────────────────────────────────────────────────────
// emailProviderApi — Deploy E. Per-tenant SMTP configuration.
// Used by Settings → Email Provider to let the merchant connect their own
// SMTP (Ionos / Sendgrid / etc) so payment confirmation / club / abandoned
// cart / support replies go out from THEIR address, not from ZeroRiskPay.
// Tracking emails always use the platform SMTP and are not affected.
// ─────────────────────────────────────────────────────────────────────────────
export const emailProviderApi = {
  get:    ()                  => api.get('/email-provider').then(r => r.data),
  save:   (cfg)               => api.put('/email-provider', cfg).then(r => r.data),
  remove: ()                  => api.delete('/email-provider').then(r => r.data),
  test:   (to)                => api.post('/email-provider/test', { to }).then(r => r.data),
};
